import { AUDIENCE_CODES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Avatar, Badge, Button, EmptyState, ErrorState, Icon, Input, Pagination, Select, StatusBadge, TableSkeletonRows } from "@sagot/ui";
import { useState } from "react";
import { useDebounced } from "../../../lib/slug";
import { isPlanGateError, UpgradeCard } from "../shared/plan-gate";
import { formatNumber, formatRelative } from "../shared/format";
import type { EndUserOut } from "../shared/types";
import { useAudienceLabel, useAudiences } from "../shared/use-shared-queries";
import { audienceTone, endUserStatusMeta } from "./audience-meta";
import { CsvImportButton } from "./csv-import";
import { EndUserDialog } from "./end-user-dialog";
import { END_USERS_PAGE_SIZE, useCanManageAudiences, useEndUsers } from "./use-audiences-data";

const COLUMNS = 8;
const SEARCH_MAX = 100;

/** Audience chips shown for a user: the non-public ones, or public when that's all they have. */
export function visibleAudiences(codes: readonly number[]): number[] {
  const rest = codes.filter((c) => c !== AUDIENCE_CODES.public);
  return rest.length > 0 ? rest : [AUDIENCE_CODES.public];
}

function UserRow({ user, onOpen }: { user: EndUserOut; onOpen: (user: EndUserOut) => void }) {
  const { t, i18n } = useTranslation();
  const label = useAudienceLabel();
  const name = user.display_name ?? t("deployKnowledge.audiences.anonymousUser");
  const status = endUserStatusMeta(user.status);
  const secondary = user.external_id_masked ?? user.email ?? user.mobile_masked;
  return (
    <tr>
      <td>
        <div className="flex items-center gap-2.5">
          <Avatar name={name} />
          <div className="min-w-0">
            <div className="font-semibold">{name}</div>
            {secondary ? <div className={user.external_id_masked ? "font-mono text-[11px] text-fg-subtle" : "text-[11px] text-fg-subtle"}>{secondary}</div> : null}
          </div>
        </div>
      </td>
      <td>{user.role_label ?? "—"}</td>
      <td className="text-fg-muted">{user.segment ?? "—"}</td>
      <td>
        <div className="flex flex-wrap gap-1">
          {visibleAudiences(user.audience_codes).map((code) => (
            <Badge key={code} tone={audienceTone(code)}>
              {label(code)}
            </Badge>
          ))}
        </div>
      </td>
      <td className="text-fg-muted">{formatRelative(user.last_active_at, i18n.language)}</td>
      <td className="text-right tabular-nums">{formatNumber(user.conversation_count, i18n.language)}</td>
      <td>
        <StatusBadge status={status.tone} icon={status.icon} label={t(`deployKnowledge.audiences.status.${user.status}`, { defaultValue: user.status })} />
      </td>
      <td className="text-right">
        <Button variant="ghost" size="sm" iconOnly aria-label={t("deployKnowledge.audiences.userActionsFor", { name })} onClick={() => { onOpen(user); }}>
          <Icon name="more-horizontal" />
        </Button>
      </td>
    </tr>
  );
}

function useFilters() {
  const [query, setQuery] = useState("");
  const [audience, setAudience] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const q = useDebounced(query.trim());
  return { query, setQuery: (v: string) => { setQuery(v); setPage(1); }, audience, setAudience: (v: number | null) => { setAudience(v); setPage(1); }, page, setPage, q };
}

/** End users tab: searchable, paginated directory with CSV import and per-user actions. */
export function EndUsersPanel() {
  const { t, i18n } = useTranslation();
  const filters = useFilters();
  const audiences = useAudiences();
  const users = useEndUsers({ q: filters.q, audience: filters.audience, page: filters.page });
  const canManage = useCanManageAudiences();
  const [open, setOpen] = useState<EndUserOut | null>(null);
  if (users.isError && isPlanGateError(users.error)) return <UpgradeCard feature={t("deployKnowledge.audiences.tabUsers")} />;
  const total = users.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / END_USERS_PAGE_SIZE));
  const items = users.data?.items ?? [];
  return (
    <section className="card overflow-hidden" aria-labelledby="users-title">
      <div className="card-header flex-wrap gap-3">
        <div>
          <h2 id="users-title" className="card-title">{t("deployKnowledge.audiences.tabUsers")}</h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("deployKnowledge.audiences.usersHint", { total: formatNumber(total, i18n.language) })}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="h-8 w-56 text-xs"
            type="search"
            maxLength={SEARCH_MAX}
            placeholder={t("deployKnowledge.audiences.usersSearch")}
            aria-label={t("deployKnowledge.audiences.usersSearch")}
            value={filters.query}
            onChange={(e) => { filters.setQuery(e.target.value); }}
          />
          <Select
            className="h-8 w-44 text-xs"
            aria-label={t("deployKnowledge.audiences.usersAudienceFilter")}
            value={filters.audience === null ? "" : String(filters.audience)}
            onChange={(e) => { filters.setAudience(e.target.value ? Number(e.target.value) : null); }}
          >
            <option value="">{t("deployKnowledge.audiences.allAudiences")}</option>
            {(audiences.data ?? []).map((a) => (
              <option key={a.code} value={a.code}>{a.label}</option>
            ))}
          </Select>
          {canManage ? <CsvImportButton /> : null}
        </div>
      </div>
      {users.isError ? (
        <ErrorState title={t("deployKnowledge.loadError")} description={t("deployKnowledge.loadErrorBody")} onRetry={() => void users.refetch()} retryLabel={t("common.retry")} />
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t("deployKnowledge.audiences.colName")}</th>
                <th>{t("deployKnowledge.audiences.colRole")}</th>
                <th>{t("deployKnowledge.audiences.colSegment")}</th>
                <th>{t("deployKnowledge.audiences.colAudience")}</th>
                <th>{t("deployKnowledge.audiences.colLastActive")}</th>
                <th className="text-right">{t("deployKnowledge.audiences.colConversations")}</th>
                <th>{t("deployKnowledge.audiences.colStatus")}</th>
                <th><span className="sr-only">{t("deployKnowledge.audiences.colActions")}</span></th>
              </tr>
            </thead>
            <tbody>
              {users.isPending ? (
                <TableSkeletonRows rows={6} columns={COLUMNS} />
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS}>
                    <EmptyState
                      icon={filters.q || filters.audience !== null ? "search" : "users"}
                      headingLevel={3}
                      title={filters.q || filters.audience !== null ? t("deployKnowledge.audiences.usersNoMatch") : t("deployKnowledge.audiences.usersEmptyTitle")}
                      description={t("deployKnowledge.audiences.usersEmptyBody", { columns: "name,email,external_id,role,segment,audiences" })}
                      action={canManage && !filters.q ? <CsvImportButton /> : undefined}
                    />
                  </td>
                </tr>
              ) : (
                items.map((user) => <UserRow key={user.id} user={user} onOpen={setOpen} />)
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-xs text-fg-subtle">
        <span>{t("deployKnowledge.audiences.usersShowing", { shown: items.length, total: formatNumber(total, i18n.language) })}</span>
        <Pagination
          page={filters.page}
          pageCount={pageCount}
          onPageChange={filters.setPage}
          labels={{
            nav: t("deployKnowledge.audiences.paginationNav"),
            previous: t("deployKnowledge.audiences.previousPage"),
            next: t("deployKnowledge.audiences.nextPage"),
            page: (n) => t("deployKnowledge.audiences.pageN", { page: n }),
            summary: (page, count) => t("deployKnowledge.audiences.pageOf", { page, count }),
          }}
        />
      </div>
      {open ? <EndUserDialog user={open} canManage={canManage} onClose={() => { setOpen(null); }} /> : null}
    </section>
  );
}
